import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { zoneLabel } from "../lib/time";
import { LoadError, SaveBar, SectionHeader } from "./Settings";
import TimezoneSelect from "../components/TimezoneSelect";

// Paramètres › Général — le fuseau horaire de l'entreprise (lot T1 de PROPOSITION_FUSEAU_HORAIRE.md). Il décide ce qu'est « aujourd'hui », « en retard »
// et « cette semaine », et l'heure des messages envoyés à un groupe. Chaque personne peut avoir le sien dans son profil.
export default function SettingsGeneral() {
  const { user, updateUser } = useAuth();
  const canManage = hasPermission(user, "settings:manage");
  const [saved, setSaved] = useState(null);
  const [value, setValue] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = (settings) => { setSaved(settings); setValue(settings.timezone); };
  const load = async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/settings/app");
      apply(data.settings);
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = saved != null && saved.timezone !== value;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/app", { timezone: value });
      apply(data.settings);
      // Whoever has no zone of their own now reads the new one: the screens follow without a reload.
      updateUser({ company_timezone: data.settings.timezone, effective_timezone: user?.timezone || data.settings.timezone });
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (saved == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-general">
      <SectionHeader title="Général" description="Les réglages qui valent pour toute l'entreprise." />
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <label htmlFor="company-timezone" className="block text-sm font-medium text-slate-900">Fuseau horaire de l'entreprise</label>
        <TimezoneSelect id="company-timezone" value={value} onChange={(zone) => zone && setValue(zone)} disabled={!canManage} data-testid="company-timezone" />
        <p className="text-xs text-slate-500" data-testid="company-timezone-now">Aujourd'hui : {zoneLabel(value)}.</p>
        <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4 border-t border-slate-100 pt-3">
          <li>C'est le fuseau de ceux qui n'ont pas choisi le leur dans leur profil : il décide « aujourd'hui », « en retard » et « cette semaine », et l'heure des messages envoyés à un groupe.</li>
          <li>Les heures déjà saisies ne bougent pas : elles sont gardées en UTC et seulement lues dans le fuseau de chacun.</li>
          <li>Heure d'été comprise : un fuseau suit sa propre règle pour chaque date, jamais un décalage fixe.</li>
        </ul>
      </div>
      <SaveBar dirty={dirty} saving={saving} canManage={canManage} onSave={save} onRevert={() => apply(saved)} />
    </div>
  );
}
